// Guías y Ayuda (apartado de Ayuda): tutorial guiado + materiales de apoyo en PDF.
//
// Disponible para CUALQUIER usuario autenticado. Agrupa dos formas de aprender a
// usar la app:
//   1. El RECORRIDO GUIADO interactivo (onboarding): se puede relanzar cuando se
//      quiera; recorre las secciones y subfunciones principales sobre la propia
//      app. El estado se persiste por usuario en el backend.
//   2. Las GUÍAS en PDF publicadas por el equipo (globales, iguales para todas
//      las cuentas). El contenido lo sirve el backend desde S3/CloudFront vía
//      `GET /guides`; aquí se muestran como tarjetas descargables.
//
// Cada guía trae un público objetivo (todos / propietario / administrador) que
// se muestra como etiqueta, para que el usuario sepa de un vistazo cuáles le
// aplican. No se ocultan: un miembro puede querer leer la de propietario para
// entender qué hace el dueño del catálogo.

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import type { GuideAudience, UserGuide } from '../api/endpoints.js';
import { useApi } from '../api/ApiContext.js';
import { ButtonSpinner, Card, ErrorNotice, Icon, PageHeader, Spinner } from '../ui/components.js';
import { useAsync } from '../ui/useAsync.js';
import { useOnboarding } from '../onboarding/OnboardingContext.js';

/** Estilo por público objetivo de la guía (la etiqueta se resuelve vía i18n). */
const AUDIENCE_STYLE: Record<GuideAudience, string> = {
  all: 'text-primary bg-primary-container/10 border-primary-container/40',
  owner: 'text-goldmine-vg bg-goldmine-vg/10 border-goldmine-vg/40',
  admin: 'text-tertiary-container bg-tertiary-container/10 border-tertiary-container/40',
};

/** Etiqueta i18n del público objetivo de una guía. */
function audienceLabel(t: TFunction, audience: GuideAudience): string {
  const key =
    audience === 'owner'
      ? 'userGuide.audienceOwner'
      : audience === 'admin'
        ? 'userGuide.audienceAdmin'
        : 'userGuide.audienceAll';
  return t(key);
}

/** Fecha legible (o cadena vacía si no se conoce / es inválida). */
function formatDate(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString();
}

/** Una tarjeta de guía descargable. */
function GuideCard({ guide }: { guide: UserGuide }) {
  const { t } = useTranslation();
  const audienceClass = AUDIENCE_STYLE[guide.audience] ?? AUDIENCE_STYLE.all;
  const dateLabel = formatDate(guide.updated_at);
  return (
    <a
      href={guide.url}
      target="_blank"
      rel="noopener noreferrer"
      className="surface-card flex items-start gap-space-md !p-space-md active:scale-[0.99] transition-transform"
    >
      <span className="w-11 h-11 shrink-0 rounded-lg bg-primary-container/15 border border-primary-container/40 text-primary flex items-center justify-center">
        <Icon name="picture_as_pdf" className="text-[24px]" />
      </span>
      <div className="flex-1 min-w-0 flex flex-col gap-0.5">
        <div className="flex items-center gap-space-xs flex-wrap">
          <span className="font-headline-sm text-headline-sm text-on-surface">{guide.title}</span>
          <span
            className={`inline-flex items-center px-2 py-0.5 rounded-full border font-badge-micro text-badge-micro uppercase tracking-wider ${audienceClass}`}
          >
            {audienceLabel(t, guide.audience)}
          </span>
        </div>
        <span className="font-body-sm text-body-sm text-on-surface-variant">
          {guide.description}
        </span>
        <span className="font-body-sm text-body-sm text-on-surface-variant mt-0.5">
          {t('userGuide.pdfMeta', { version: guide.version })}
          {dateLabel ? ` · ${dateLabel}` : ''}
        </span>
      </div>
      <span className="w-9 h-9 shrink-0 self-center flex items-center justify-center rounded-lg bg-surface-rack text-primary">
        <Icon name="download" className="text-[20px]" />
      </span>
    </a>
  );
}

export function UserGuideView() {
  const { t } = useTranslation();
  const api = useApi();
  const guides = useAsync(() => api.guides.list(), []);
  const items = guides.data?.items ?? [];

  return (
    <div className="flex flex-col">
      <PageHeader title={t('userGuide.pageTitle')} subtitle={t('userGuide.pageSubtitle')} back />

      <div className="flex flex-col px-margin-mobile py-space-md gap-space-md">
        {/* Recorrido guiado interactivo: relanzable en cualquier momento. */}
        <OnboardingCard />

        {/* Separador antes de los manuales en PDF. */}
        <div className="flex items-center gap-space-sm mt-space-xs">
          <h2 className="font-headline-sm text-headline-sm font-bold uppercase tracking-widest text-vacuum-glow">
            {t('userGuide.pdfSectionTitle')}
          </h2>
          <span className="flex-1 h-px bg-gradient-to-r from-vacuum-glow/40 to-transparent" />
        </div>

        {guides.loading && <Spinner label={t('userGuide.loading')} />}
        {guides.error && <ErrorNotice message={String(guides.error)} onRetry={guides.reload} />}

        {!guides.loading && !guides.error && items.length === 0 && (
          <Card title={t('userGuide.emptyTitle')} icon="menu_book">
            <p className="font-body-sm text-body-sm text-on-surface-variant">
              {t('userGuide.emptyBody')}
            </p>
          </Card>
        )}

        {items.length > 0 && (
          <div className="flex flex-col gap-space-sm">
            {items.map((g) => (
              <GuideCard key={g.slug} guide={g} />
            ))}
          </div>
        )}

        <p className="font-body-sm text-body-sm text-on-surface-variant px-space-xs">
          {t('userGuide.footer')}
        </p>
      </div>
    </div>
  );
}

/**
 * Tarjeta del RECORRIDO GUIADO: inicia (o reinicia) el tour interactivo. Pone el
 * estado del onboarding en "pendiente" en el backend (vale en todos los
 * dispositivos del usuario) y lo relanza de inmediato sobre la app. Disponible
 * para cualquier usuario autenticado.
 */
function OnboardingCard() {
  const { t } = useTranslation();
  const { restart } = useOnboarding();
  const [busy, setBusy] = useState(false);

  async function onRestart() {
    setBusy(true);
    try {
      await restart();
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card title={t('userGuide.onboardingTitle')} icon="tips_and_updates">
      <p className="font-body-sm text-body-sm text-on-surface-variant mb-space-md">
        {t('userGuide.onboardingHint')}
      </p>
      <button
        type="button"
        className="btn-primary btn-sm self-start"
        disabled={busy}
        onClick={() => void onRestart()}
      >
        {busy ? <ButtonSpinner /> : <Icon name="play_circle" className="text-[16px]" />}
        {t('userGuide.onboardingStart')}
      </button>
    </Card>
  );
}
